import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, ArrowUp, ArrowDown, Rewind } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = { date: "2026-06-22", label: "", url: "", sort_order: 0 };

function formatDay(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function ReplaysAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const r = await api.get("/replays"); setRows(r.data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ date: r.date, label: r.label, url: r.url, sort_order: r.sort_order || 0 });
    setOpen(true);
  };
  const save = async () => {
    try {
      const payload = {
        date: (form.date || "").trim(),
        label: (form.label || "").trim(),
        url: (form.url || "").trim(),
        sort_order: Number(form.sort_order) || 0,
      };
      if (!payload.date || !payload.label || !payload.url) {
        toast.error("Date, label and URL are required");
        return;
      }
      if (editing) await api.patch(`/replays/${editing.id}`, payload);
      else await api.post("/replays", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.label}"?`)) return;
    try { await api.delete(`/replays/${r.id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const move = async (r, direction) => {
    // Find siblings within same date
    const siblings = rows.filter((x) => x.date === r.date);
    const idx = siblings.findIndex((x) => x.id === r.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= siblings.length) return;
    const a = siblings[idx], b = siblings[target];
    const aOrder = a.sort_order || 0;
    const bOrder = b.sort_order || 0;
    try {
      const aPayload = { date: a.date, label: a.label, url: a.url, sort_order: aOrder === bOrder ? (direction === "up" ? bOrder - 1 : bOrder + 1) : bOrder };
      const bPayload = { date: b.date, label: b.label, url: b.url, sort_order: aOrder };
      await api.patch(`/replays/${a.id}`, aPayload);
      await api.patch(`/replays/${b.id}`, bPayload);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Group by date for display
  const byDate = rows.reduce((acc, r) => {
    if (!acc[r.date]) acc[r.date] = [];
    acc[r.date].push(r);
    return acc;
  }, {});
  const dates = Object.keys(byDate).sort();

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Button onClick={openNew} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="admin-new-replay-btn">
          <Plus size={16} className="mr-1" /> New replay
        </Button>
        <p className="text-xs text-[#7A828C] ml-2">
          One row per Vimeo recording (typically 4-6 per day - the morning/afternoon segments from each stream).
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="p-6 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm text-sm text-[#7A828C] text-center">
          No replays yet. Tap <strong>New replay</strong> to add your first Vimeo link.
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((d) => (
            <div key={d} className="space-y-2">
              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] px-1">
                {formatDay(d)}
              </div>
              {byDate[d].map((r, i, arr) => (
                <div key={r.id} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-3" data-testid={`admin-replay-row-${r.id}`}>
                  <div className="w-9 h-9 grid place-items-center bg-[#000000] text-[#43C4E3] rounded-sm flex-shrink-0">
                    <Rewind size={14} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.label}</div>
                    <div className="text-xs text-[#7A828C] truncate font-mono-time">{r.url}</div>
                  </div>
                  {arr.length > 1 && (
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button type="button" onClick={() => move(r, "up")} disabled={i === 0} className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] disabled:opacity-30 disabled:hover:bg-transparent" aria-label="Move up" data-testid={`move-replay-up-${r.id}`}>
                        <ArrowUp size={14} />
                      </button>
                      <button type="button" onClick={() => move(r, "down")} disabled={i === arr.length - 1} className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#F0F2F7] disabled:opacity-30 disabled:hover:bg-transparent" aria-label="Move down" data-testid={`move-replay-down-${r.id}`}>
                        <ArrowDown size={14} />
                      </button>
                    </div>
                  )}
                  <button onClick={() => openEdit(r)} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7]" data-testid={`edit-replay-${r.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => del(r)} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7] text-red-400" data-testid={`delete-replay-${r.id}`}><Trash size={16} /></button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0]">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit replay" : "New replay"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm font-mono-time" data-testid="form-replay-date" />
            </div>
            <div>
              <Label>Label</Label>
              <Input placeholder='e.g. "Plenary - Morning" or "Concurrent 2 - Afternoon"' value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-sm" data-testid="form-replay-label" />
              <p className="text-[11px] text-[#7A828C] mt-1">Shown to delegates. Keep it short and descriptive.</p>
            </div>
            <div>
              <Label>Vimeo URL</Label>
              <Input placeholder="https://vimeo.com/123… or full <iframe> embed" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="rounded-sm font-mono-time text-xs" data-testid="form-replay-url" />
              <p className="text-[11px] text-[#7A828C] mt-1">vimeo.com/123, player.vimeo.com/video/123, or paste the full embed code - all work.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="form-replay-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
